import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Plus, RotateCcw, Clock, PackageCheck, PackageX, CheckCircle2,
  Pencil, Trash2, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/kit/StatCard";
import { SearchInput } from "@/components/kit/SearchInput";
import { FilterShell } from "@/components/kit/FilterShell";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { ExportButton } from "@/components/kit/ExportButton";
import { DataTable } from "@/components/tables/DataTable";
import { RowActions } from "@/components/tables/RowActions";
import { EmptyState } from "@/components/feedback/EmptyState";
import { TableSkeleton } from "@/components/feedback/Skeletons";
import { ConfirmDialog } from "@/components/feedback/ConfirmDialog";
import { ReturnDrawer } from "./ReturnDrawer";
import { ReturnConfirmDrawer } from "./ReturnConfirmDrawer";
import { returnsService, storesService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { formatRelativeTime } from "@/lib/format";
import { useAuth } from "@/hooks/useAuth";
import { canWrite, isOwnerRole } from "@/lib/permissions/permissions";
import { RETURN_STATUS_META, RETURN_CONDITION_META } from "@/lib/constants";

const DEFAULT_FILTERS = { search: "", store: "all", status: "all", condition: "all", date_from: "", date_to: "" };

export default function ReturnsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const writable = canWrite(user?.role, "returns");
  const owner = isOwnerRole(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();

  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    const status = searchParams.get("status");
    if (status) {
      setDraft((d) => ({ ...d, status }));
      setApplied((a) => ({ ...a, status }));
    }
    if (searchParams.get("new") === "1" && writable) {
      setEditing(null);
      setDrawerOpen(true);
    }
    if (status || searchParams.get("new")) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const params = useMemo(() => ({
    search: applied.search,
    store_id: applied.store === "all" ? "" : applied.store,
    status: applied.status === "all" ? "" : applied.status,
    condition: applied.condition === "all" ? "" : applied.condition,
    date_from: applied.date_from,
    date_to: applied.date_to,
  }), [applied]);

  const listQuery = useQuery({ queryKey: ["returns", params], queryFn: () => returnsService.list(params) });
  const summaryQuery = useQuery({ queryKey: ["returns", "summary"], queryFn: returnsService.summary });
  const storesQuery = useQuery({ queryKey: ["stores", "lite"], queryFn: () => storesService.list() });

  const stores = useMemo(() => storesQuery.data?.items || [], [storesQuery.data]);
  const storeMap = useMemo(() => Object.fromEntries(stores.map((s) => [s.id, s.store_name])), [stores]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["returns"] });
    qc.invalidateQueries({ queryKey: ["inventory"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["activity"] });
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openView = (r) => { setEditing(r); setDrawerOpen(true); };

  const cardFilter = (status) => {
    const next = applied.status === status ? "all" : status;
    setDraft((d) => ({ ...d, status: next, condition: "all" }));
    setApplied((a) => ({ ...a, status: next, condition: "all" }));
  };

  const conditionFilter = (condition) => {
    const next = applied.condition === condition ? "all" : condition;
    setDraft((d) => ({ ...d, condition: next, status: "all" }));
    setApplied((a) => ({ ...a, condition: next, status: "all" }));
  };

  const confirmDelete = async () => {
    try { await returnsService.remove(toDelete.id); toast.success("Return deleted"); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setToDelete(null); }
  };

  const s = summaryQuery.data || {};
  const rows = listQuery.data?.items || [];

  const rowMenu = (r) => {
    const items = [];
    if (writable && r.status === "pending") {
      items.push({ key: "confirm", label: "Confirm Return", icon: PackageCheck, onSelect: () => setConfirming(r) });
      items.push({ key: "edit", label: "Edit", icon: Pencil, onSelect: () => openView(r) });
    } else {
      items.push({ key: "view", label: "View details", icon: Eye, onSelect: () => openView(r) });
    }
    if (owner) {
      items.push({ key: "delete", label: "Delete", icon: Trash2, destructive: true, separatorBefore: true, onSelect: () => setToDelete(r) });
    }
    return items;
  };

  const columns = [
    { key: "return_id", header: "Return ID", render: (r) => <span className="font-medium text-foreground">{r.return_id}</span> },
    { key: "store", header: "Store", render: (r) => storeMap[r.store_id] || <span className="text-muted-foreground">—</span> },
    { key: "tracking_number", header: "Tracking", render: (r) => r.tracking_number || <span className="text-muted-foreground">—</span> },
    { key: "order_reference", header: "Order Ref", render: (r) => r.order_reference || <span className="text-muted-foreground">—</span> },
    { key: "sku_count", header: "SKUs", align: "right", render: (r) => <span className="tabular-nums">{r.products?.length || 0}</span> },
    {
      key: "condition", header: "Condition",
      render: (r) => { const m = RETURN_CONDITION_META[r.condition]; return m ? <StatusBadge label={m.label} tone={m.tone} /> : <span className="text-muted-foreground">—</span>; },
    },
    {
      key: "status", header: "Status",
      render: (r) => { const m = RETURN_STATUS_META[r.status] || RETURN_STATUS_META.pending; return <StatusBadge label={m.label} tone={m.tone} />; },
    },
    { key: "updated", header: "Updated", render: (r) => formatRelativeTime(r.updated_at) },
    { key: "actions", header: "", align: "right", render: (r) => <RowActions items={rowMenu(r)} /> },
  ];

  const exportColumns = [
    { label: "Return ID", key: "return_id" },
    { label: "Store", get: (r) => storeMap[r.store_id] || "" },
    { label: "Tracking", key: "tracking_number" },
    { label: "Order Ref", key: "order_reference" },
    { label: "SKUs", get: (r) => r.products?.length || 0 },
    { label: "Condition", get: (r) => RETURN_CONDITION_META[r.condition]?.label || "" },
    { label: "Status", get: (r) => RETURN_STATUS_META[r.status]?.label || r.status },
    { label: "Created", get: (r) => r.created_at },
  ];

  return (
    <div className="space-y-6" data-testid="page-returns">
      <PageHeader
        sticky title="Returns" description="Log returned shipments and restock sellable inventory."
        actions={
          <div className="flex items-center gap-2">
            <ExportButton filename="returns" columns={exportColumns} rows={rows} testId="returns-export" />
            {writable && (
              <Button className="gap-2" onClick={openCreate} data-testid="returns-primary-action"><Plus className="h-4 w-4" />New Return</Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-pending" label="Pending" value={s.pending ?? "—"} icon={Clock} tone="amber" onClick={() => cardFilter("pending")} active={applied.status === "pending"} />
        <StatCard testId="stat-sellable" label="Sellable" value={s.sellable ?? "—"} icon={PackageCheck} tone="green" onClick={() => conditionFilter("sellable")} active={applied.condition === "sellable"} />
        <StatCard testId="stat-non-sellable" label="Non-Sellable" value={s.non_sellable ?? "—"} icon={PackageX} tone="red" onClick={() => conditionFilter("non_sellable")} active={applied.condition === "non_sellable"} />
        <StatCard testId="stat-confirmed" label="Confirmed" value={s.confirmed ?? "—"} icon={CheckCircle2} tone="blue" onClick={() => cardFilter("confirmed")} active={applied.status === "confirmed"} />
      </div>

      <FilterShell
        onApply={() => { setApplied(draft); toast.success("Filters applied"); }}
        onReset={() => { setDraft(DEFAULT_FILTERS); setApplied(DEFAULT_FILTERS); toast("Filters reset"); }}
      >
        <SearchInput value={draft.search} onChange={(v) => setDraft((d) => ({ ...d, search: v }))} placeholder="Return ID, tracking, SKU…" />
        <Select value={draft.store} onValueChange={(v) => setDraft((d) => ({ ...d, store: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-store"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All stores</SelectItem>{stores.map((st) => (<SelectItem key={st.id} value={st.id}>{st.store_name}</SelectItem>))}</SelectContent>
        </Select>
        <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={draft.condition} onValueChange={(v) => setDraft((d) => ({ ...d, condition: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-condition"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All conditions</SelectItem>
            <SelectItem value="sellable">Sellable</SelectItem>
            <SelectItem value="non_sellable">Non-Sellable</SelectItem>
            <SelectItem value="mixed">Mixed</SelectItem>
          </SelectContent>
        </Select>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">From</label>
          <input type="date" value={draft.date_from} onChange={(e) => setDraft((d) => ({ ...d, date_from: e.target.value }))}
            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm shadow-soft focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" data-testid="filter-date-from" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">To</label>
          <input type="date" value={draft.date_to} onChange={(e) => setDraft((d) => ({ ...d, date_to: e.target.value }))}
            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm shadow-soft focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" data-testid="filter-date-to" />
        </div>
      </FilterShell>

      {listQuery.isLoading ? (
        <TableSkeleton rows={6} cols={8} />
      ) : (
        <div className="overflow-x-auto">
          <DataTable
            testId="returns-table"
            paginated
            columns={columns}
            data={rows}
            emptyState={
              <EmptyState icon={RotateCcw} title="No returns yet" testId="returns-empty-state"
                description="Log a returned shipment to track its condition and restock sellable stock."
                action={writable ? <Button className="gap-2" onClick={openCreate} data-testid="returns-empty-action"><Plus className="h-4 w-4" /> New Return</Button> : null} />
            }
          />
        </div>
      )}

      <ReturnDrawer open={drawerOpen} onOpenChange={setDrawerOpen} record={editing} stores={stores} writable={writable} onSaved={refresh} />
      <ReturnConfirmDrawer open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)} record={confirming} storeName={confirming ? storeMap[confirming.store_id] : ""} onSaved={refresh} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}
        title="Delete return?" description={`This permanently removes "${toDelete?.return_id}" and is recorded in the Activity Log.`}
        confirmLabel="Delete return" destructive onConfirm={confirmDelete} />
    </div>
  );
}
